// packages/core/src/learning/school-modules/buffett-munger.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'buffett-munger' school. LearningModule, surface 'school'.
//   module id   = 'school-buffett-munger'  (matches exhibit nativeModuleId)
//   homeSchool  = 'buffett-munger'
// PROMOTED: five authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_BUFFETT_MUNGER_MODULE: LearningModule = {
	id: 'school-buffett-munger',
	title: 'Buffett / Munger',
	description:
		'The lens that reads investing as business ownership — quality over activity, avoiding stupidity, the circle of competence, and patience with a reason.',
	emoji: '🏛️',
	accentColor: '#1b4f8a',
	tier: 'growth',
	pillar: 'long-term-investing',
	order: 0,
	surface: 'school',
	homeSchool: 'buffett-munger',
	lessons: [
		{
			id: 'school-buffett-munger-01',
			title: 'Quality beats constant activity',
			description:
				'This lesson teaches why the Buffett and Munger lens values business quality, patience, and judgment over constant investing motion.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['buffett-munger'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'buffett-munger',
				'quality-over-activity',
				'patient-judgment',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'quality over activity',
				'business ownership lens',
				'patient judgment',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Many people treat investing like something that rewards constant motion. More checking, more reacting, more opinions, more trades, more predictions. The Buffett and Munger lens notices that activity can feel smart while quietly making judgment worse.',
				},
				{
					type: 'paragraph',
					text: 'This worldview sees investing as business ownership first. It asks whether the business has strong economics, honest incentives, durable advantages, and a price that leaves room for being wrong. The goal is not to look busy. The goal is to make fewer decisions that are better thought through.',
				},
				{
					type: 'bullet_list',
					items: [
						'A business must make economic sense',
						'Management incentives matter',
						'Patience matters only when the underlying choice deserves patience',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is confusing action with progress.',
				},
				{
					type: 'example',
					text: 'Someone may jump between hot ideas every few weeks because each one sounds exciting. Another person may study fewer opportunities and reject most of them. The second person may look less active, but this lens would say they may be doing the harder work.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that judging quality is difficult. It is easy to say a business is great after the story is popular. It is harder to know whether the economics, competition, price, and risks actually support that belief.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, use this lens to question your relationship with activity. When you test a future scenario, ask whether constant tinkering is helping your plan or just making you feel involved.',
				},
				{
					type: 'key_takeaway',
					text: 'Good judgment often looks boring from the outside.',
				},
			],
			audioScript:
				'The Buffett and Munger lens starts with a strong push against activity for its own sake. A lot of investing content makes you feel like you should always be doing something. Check the market. Watch the news. Find the next idea. React to the next headline. Move in. Move out. Compare yourself to everyone who seems faster. That can feel productive, but this worldview is suspicious of that feeling.\n\nBuffett and Munger thinking treats investing less like a video game and more like business ownership. The question is not, what is moving today? The question is, what am I actually owning, and why should it still make sense after the excitement fades? Is this a business with strong economics? Does it have a reason to keep earning well over time? Are the people running it aligned with owners? Is the price reasonable enough that I am not depending on a perfect future? Do I understand the main risks, or am I just repeating a good story?\n\nThis lens protects you from confusing motion with progress. In investing, motion is emotionally rewarding. It gives you the feeling of control. You clicked something. You changed something. You responded. But activity can also hide weak judgment. Sometimes the harder move is to wait, study, reject, and do nothing. That does not feel dramatic, but it may be the discipline the lens is trying to teach.\n\nImagine two people. One is constantly finding new ideas. Every week, there is a new stock, trend, theme, or hot business. They always have an explanation. They sound informed. The other person studies fewer opportunities. They pass on most things. They do not need an opinion on every company. They are willing to say, I do not understand this well enough. From the outside, the first person may look sharper. Buffett and Munger thinking would often respect the second person more because they are protecting judgment.\n\nBut here is the risk. This lens can make people sound wiser than they are. It is easy to say quality, moat, management, and patience. It is much harder to actually judge those things. A popular company can sound like quality because everyone already likes it. A cheap company can sound attractive because the price looks low. A charismatic leader can make risk feel smaller than it is. The danger is not only bad analysis. The danger is overconfidence dressed up as patience.\n\nSo the point is not to copy famous investors or pretend you have their skill. The point is to learn the behavior of the lens. Slow down. Ask better questions. Separate business quality from market excitement. Separate understanding from familiarity. Separate patience from stubbornness.\n\nInside Nisk, this matters because your plan should not depend on constant clever action. Use Simulate to compare a steadier path against one that assumes you always make the right moves. Ask whether tinkering is actually improving your future or just making you feel active. Good judgment often looks boring from the outside. That is part of why it is hard.',
			flashcards: [
				{
					id: 'school-buffett-munger-01-fc-1',
					role: 'main_idea',
					front: 'What does this lens value?',
					back: 'It values business quality, patience, incentives, and judgment over constant activity.',
				},
				{
					id: 'school-buffett-munger-01-fc-2',
					role: 'watch_out',
					front: 'What is the activity trap?',
					back: 'Believing more movement means better investing judgment.',
				},
				{
					id: 'school-buffett-munger-01-fc-3',
					role: 'use_it',
					front: 'How do you apply this lens?',
					back: 'Ask whether action improves the plan or only makes you feel involved.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Compare activity with patience',
			},
			revision: 1,
		},
		{
			id: 'school-buffett-munger-02',
			title: 'Avoiding stupidity is a strategy',
			description:
				'This lesson teaches why Buffett and Munger thinking puts heavy weight on avoiding obvious mistakes before chasing brilliant moves.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['buffett-munger'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['buffett-munger', 'inversion', 'mistake-avoidance'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'inversion thinking',
				'mistake avoidance',
				'survival advantage',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Most people want the brilliant move. The rare insight. The perfect call. The Buffett and Munger lens starts somewhere less glamorous: avoid the obvious ways people blow themselves up.',
				},
				{
					type: 'paragraph',
					text: 'This worldview believes avoiding stupidity is not weakness. It is strategy. If you can avoid fragile debt, bad incentives, emotional decisions, overconfidence, and things you do not understand, you give good decisions more time to work.',
				},
				{
					type: 'bullet_list',
					items: [
						'Avoid what can ruin you',
						'Watch incentives closely',
						'Do not need an opinion on everything',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is trying to be brilliant before learning how not to be careless.',
				},
				{
					type: 'example',
					text: 'Someone may chase a complex investment because it sounds sophisticated. This lens would first ask what can go wrong, who benefits if you misunderstand it, and whether you are being paid enough for the risk.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that mistake avoidance can become fear if taken too far. Avoiding bad decisions matters, but never acting, never learning, and never taking measured risk can also keep you stuck.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, this lens fits a written reflection. Before getting excited about an opportunity, invert it. Ask how this could fail, what would make you regret it, and what you might be missing.',
				},
				{
					type: 'key_takeaway',
					text: 'Sometimes the best edge is refusing the obvious mistake.',
				},
			],
			audioScript:
				'Buffett and Munger thinking has a phrase at the center of it: invert. In plain language, that means do not only ask how to succeed. Ask how people fail, then try not to do those things. It sounds simple, but it changes the whole mood of decision making.\n\nMost people want to be brilliant. They want the great call, the rare insight, the move nobody else saw. This lens is much more interested in not doing dumb things. That may sound negative, but it is practical. A few bad mistakes can erase years of decent behavior. A fragile decision can force you out at the worst time. A complex investment you do not understand can create risk you only notice after it hurts. A bad incentive can quietly move the game against you.\n\nThe core idea is that survival has value. If you avoid the major ways people blow themselves up, you give your good decisions time to matter. You do not need to win every round. You do not need to have an opinion on every opportunity. You do not need to chase every trend. You need to avoid the choices that can permanently damage your freedom path.\n\nThink about a complex investment that sounds sophisticated. The pitch is polished. The returns look impressive. The person selling it sounds confident. A lot of people would ask, how much can I make? This lens asks different questions first. How can this fail? What part do I not understand? Who gets paid even if I lose? What has to go right for this to work? What happens if the timing is bad? Can this decision hurt me badly if I am wrong?\n\nThat is not cynicism. It is protection. Buffett and Munger thinking pays attention to incentives because people often behave according to how they are rewarded. If someone makes money when you buy, they may not be neutral. If management gets rewarded for growth at any cost, they may make choices that look good now and hurt later. If you get social status from sounding bold, your own ego becomes an incentive too.\n\nThe tradeoff is that mistake avoidance can turn into fear. If every possible downside makes you freeze, you may never learn, never invest, never build, never grow, and never make a decision. That is not the point. The point is not to avoid all risk. The point is to avoid risks you do not understand, risks that can ruin you, and risks where the upside does not justify the fragility.\n\nIn Nisk, this is a strong reflection lens. Before you get excited, invert the decision. Write down how it could go wrong. Name the incentive. Name the part you do not understand. Name the condition that would make you walk away. This does not tell you what to buy or avoid. It trains the judgment muscle.\n\nSometimes the best edge is not genius. Sometimes the edge is refusing the obvious mistake while other people get pulled in by confidence, complexity, and excitement.',
			flashcards: [
				{
					id: 'school-buffett-munger-02-fc-1',
					role: 'main_idea',
					front: 'What does inversion ask?',
					back: 'It asks how a decision can fail before asking how it can win.',
				},
				{
					id: 'school-buffett-munger-02-fc-2',
					role: 'watch_out',
					front: 'What is the brilliance trap?',
					back: 'Chasing a genius move before learning to avoid obvious mistakes.',
				},
				{
					id: 'school-buffett-munger-02-fc-3',
					role: 'use_it',
					front: 'How do you invert a choice?',
					back: 'Ask what can go wrong, who benefits, and what you do not understand.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Invert the opportunity',
				prompt: 'Write three ways this opportunity could fail before writing why it might work.',
			},
			revision: 1,
		},
		{
			id: 'school-buffett-munger-03',
			title: 'Your circle has edges',
			description:
				'This lesson teaches why Buffett and Munger thinking treats knowing what you do not understand as a strength.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['buffett-munger'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'buffett-munger',
				'circle-of-competence',
				'false-confidence',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'circle of competence',
				'understanding boundary',
				'false confidence',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'One of the hardest things in money is admitting where your understanding stops. The Buffett and Munger lens respects that boundary. It calls it your circle of competence.',
				},
				{
					type: 'paragraph',
					text: 'This worldview believes you do not need to understand everything. You need to understand the difference between what you know, what you kind of know, and what you are pretending to know because the story sounds good.',
				},
				{
					type: 'bullet_list',
					items: [
						'Familiar is not the same as understood',
						'Popular is not the same as clear',
						'Confidence is not the same as competence',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is expanding your circle in your imagination faster than you expand it through real learning.',
				},
				{
					type: 'example',
					text: 'You may use a product every day and still not understand the business model, competition, margins, risks, or price. Familiarity can make the company feel safer than it really is.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that staying inside your circle can become a comfort zone. The goal is not to stay ignorant. The goal is to learn honestly and admit when you are not there yet.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, this lens becomes a self check. Write what you truly understand, what you are learning, and what you should avoid pretending to understand.',
				},
				{
					type: 'key_takeaway',
					text: 'Knowing the edge of your understanding protects you from expensive confidence.',
				},
			],
			audioScript:
				'The circle of competence is one of the most useful ideas in the Buffett and Munger lens because it is brutally honest. It says you do not need to understand everything. You do not need an opinion on every market, every company, every asset, every trend, every strategy, or every opportunity. But you do need to know what you actually understand.\n\nThat sounds easy until money and ego get involved. A lot of people confuse familiarity with understanding. They use a product every day, so they assume they understand the business. They like the brand, so they assume the company is strong. They see everyone talking about it, so they assume the opportunity is obvious. They read a few threads, watch a few videos, hear a few smart sounding phrases, and suddenly the idea feels clear. But feeling familiar is not the same as understanding economics, competition, price, risk, and incentives.\n\nThis lens protects you from false confidence. Your circle of competence is not a list of things you have heard of. It is the area where you can explain what matters, what can go wrong, what the business depends on, and why the price may or may not make sense. If you cannot explain the main moving parts in plain language, the lens would push you to be careful.\n\nImagine someone who loves a certain technology company. They use the product constantly. They admire the founder. They see the brand everywhere. They may say, I understand this company. But do they understand the margins? The competitive threats? The valuation? The customer concentration? The regulation risk? The assumptions already built into the price? Maybe they do. Maybe they do not. The point is that product love is not the same as investment understanding.\n\nThe common mistake is letting your circle grow in your imagination faster than it grows through real learning. A rising price can make you feel smarter. A popular story can make you feel safer. A confident community can make you feel like the work has already been done. Buffett and Munger thinking cuts through that. It would rather you say, I do not know, than pretend understanding because pretending can get expensive.\n\nThe tradeoff is important. Your circle should not become a prison. If you only stay with what you already know, you may stop learning. The goal is not to be narrow forever. The goal is to expand slowly and honestly. Read. Study. Compare. Ask what you missed. Learn how the economics work. Learn the failure modes. But until you really understand something, do not give yourself fake credit.\n\nInside Nisk, this is a reflection action. Write three areas you understand well. Then write three areas where you are tempted to sound smarter than you are. That second list may protect you more than the first. Knowing the edge of your understanding is not weakness. It is one of the strongest forms of risk control.',
			flashcards: [
				{
					id: 'school-buffett-munger-03-fc-1',
					role: 'main_idea',
					front: 'What is your circle?',
					back: 'It is the area where you genuinely understand the important moving parts.',
				},
				{
					id: 'school-buffett-munger-03-fc-2',
					role: 'watch_out',
					front: 'What is false confidence?',
					back: 'Mistaking familiarity, popularity, or excitement for real understanding.',
				},
				{
					id: 'school-buffett-munger-03-fc-3',
					role: 'use_it',
					front: 'How do you use the circle?',
					back: 'Name what you understand, what you are learning, and what you should not fake.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Define your circle',
				prompt: 'Write three things you understand well and three things you are tempted to fake.',
			},
			revision: 1,
		},
		{
			id: 'school-buffett-munger-04',
			title: 'Patience needs a reason',
			description:
				'This lesson teaches why Buffett and Munger thinking separates true patience from stubborn attachment to a bad idea.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['buffett-munger'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['buffett-munger', 'patient-ownership', 'thesis-discipline'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'patient ownership',
				'stubbornness risk',
				'thesis discipline',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Patience sounds wise, but patience is not automatically good. Waiting with a strong reason is different from refusing to admit you were wrong. The Buffett and Munger lens takes that difference seriously.',
				},
				{
					type: 'paragraph',
					text: 'This worldview believes patience works when it is attached to quality, understanding, fair price, and a clear reason for ownership. It is not patience to sit with a weak idea just because selling would bruise your ego.',
				},
				{
					type: 'bullet_list',
					items: [
						'Know why you are waiting',
						'Know what would change your mind',
						'Know when patience has become denial',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is using patience as a nicer word for stubbornness.',
				},
				{
					type: 'example',
					text: 'Someone buys a business because they believe it has durable advantages. If the facts change, the lens asks them to update. Holding forever only makes sense when the reason still makes sense.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens asks for a level of judgment many users may not have yet. Without clear thinking, patience can become a story that excuses inaction.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, connect patience to your broader plan. Build a plan that does not require perfect timing, then write what would make you rethink a decision before emotion takes over.',
				},
				{
					type: 'key_takeaway',
					text: 'Patience is powerful only when the reason still holds.',
				},
			],
			audioScript:
				'Buffett and Munger are famous for patience, but this lesson needs to be clear: patience is not automatically wisdom. Waiting can be smart. Waiting can also be denial. The difference is whether you still have a good reason.\n\nThis lens believes patience should be attached to quality, understanding, price, and a clear thesis. A thesis is just your reason for believing an investment or decision makes sense. What are you counting on? What has to remain true? What would make you change your mind? If you cannot answer those questions, your patience may not be patience. It may just be emotional attachment.\n\nA lot of people love the word long term because it makes almost any decision sound mature. A bad idea can hide behind long term language. A weak business can be defended with patience. A falling price can be excused with conviction. A mistake can become part of your identity. The Buffett and Munger lens pushes against that. It respects patience, but it also respects facts.\n\nImagine someone buys into a company because they believe it has durable advantages, strong management, and a reasonable price. Over time, the facts change. The advantage weakens. Management starts making choices that do not serve owners. Competition gets stronger. The price no longer makes sense compared with the outlook. At that point, holding forever may not be discipline. It may be refusal to update.\n\nThe common mistake is using patience as a nicer word for stubbornness. Stubbornness says, I do not want to be wrong. Patience says, I understand the reason, and the reason still holds. Those are different. One is ego protection. The other is discipline.\n\nThis lens also asks you to think before pressure arrives. When prices move, emotions get louder. If you do not know what would change your mind, you may make the rules up in the moment. That is dangerous. Good judgment often means deciding the rules before fear, greed, embarrassment, or social pressure start talking.\n\nThe tradeoff is that this lens is hard. It assumes you can judge whether the reason still holds. That is not easy. Many people are not ready to evaluate business quality, management behavior, competitive advantage, and valuation. That does not make them bad with money. It just means this school requires humility. If you do not have the skill, the most honest move may be to learn more, simplify, or avoid pretending.\n\nInside Nisk, this lesson connects to building a plan. A good plan should not depend on perfect timing or constant emotional decisions. Use the bigger picture. What are you trying to build? How much uncertainty can you handle? What would make you rethink a choice? Write that before the moment gets heated.\n\nThe takeaway is simple: patience is powerful only when the reason still holds. If the reason is gone, patience may just be pride with better branding.',
			flashcards: [
				{
					id: 'school-buffett-munger-04-fc-1',
					role: 'main_idea',
					front: 'When does patience work?',
					back: 'When it is tied to quality, understanding, fair price, and a reason that still holds.',
				},
				{
					id: 'school-buffett-munger-04-fc-2',
					role: 'watch_out',
					front: 'What is stubborn patience?',
					back: 'Holding on because admitting a mistake feels painful.',
				},
				{
					id: 'school-buffett-munger-04-fc-3',
					role: 'use_it',
					front: 'How do you test patience?',
					back: 'Write what would change your mind before emotion takes over.',
				},
			],
			applyAction: {
				type: 'buildFreedomPlan',
				label: 'Build a patient plan',
			},
			revision: 1,
		},
		{
			id: 'school-buffett-munger-05',
			title: 'Where this lens gets dangerous',
			description:
				'This lesson teaches how Buffett and Munger ideas can be misused when people copy the language without the skill.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['buffett-munger'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: [
				'buffett-munger',
				'copied-wisdom-risk',
				'story-over-analysis',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'copied wisdom risk',
				'skill mismatch',
				'story over analysis',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'The Buffett and Munger lens is respected for good reasons, but it can become dangerous when people copy the vocabulary without copying the discipline. Quality, moat, patience, and temperament can turn into slogans.',
				},
				{
					type: 'paragraph',
					text: 'This worldview breaks down when users overestimate their ability to analyze businesses. It also breaks down when they use famous principles to justify concentrated bets, ignore price, or dismiss risks they do not understand.',
				},
				{
					type: 'bullet_list',
					items: [
						'A good company can be a bad investment',
						'A simple quote can hide complex judgment',
						'Famous wisdom does not remove personal limits',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is sounding like Buffett and Munger while behaving like a speculator.',
				},
				{
					type: 'example',
					text: 'Someone may say they are buying a wonderful business for the long term, but they may not understand valuation, competition, capital needs, or what would prove them wrong.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens asks for rare skill and rare temperament. Many people may benefit from the ideas without needing to copy the strategy directly.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, use this lens with humility. Test your assumptions, write your limits, and compare any investing worldview against your real plan instead of treating it like an identity.',
				},
				{
					type: 'key_takeaway',
					text: 'Borrow the judgment, not the costume.',
				},
			],
			audioScript:
				'The Buffett and Munger lens is powerful, but it is also easy to misuse. That is true of many respected ideas. The better the language sounds, the easier it is to borrow the words without doing the work. Quality. Moat. Patience. Temperament. Circle of competence. Margin of safety. These are useful ideas, but they can become costumes if they are not connected to real judgment.\n\nThe danger is that people can sound like Buffett and Munger while behaving like speculators. They may say they are buying a wonderful business for the long term, but underneath that sentence, they may be chasing a popular stock, ignoring valuation, and hoping the story stays exciting. They may say they have conviction, when what they really have is social proof. They may say they are patient, when they are actually refusing to question a mistake.\n\nThis lens breaks down when users overestimate their skill. Business analysis is hard. Understanding a company is hard. Understanding competition is hard. Understanding price is hard. Understanding management incentives is hard. Even smart people get it wrong. Even professionals get it wrong. If you take famous principles and assume they automatically make you skilled, the lens can become dangerous.\n\nA good company can still be a bad investment if the price already assumes too much. A cheap company can still be a trap if the business is weak. A strong brand can still face risks. A simple story can hide complicated economics. A famous quote can make you feel calm without making the decision sound.\n\nThere is also concentration risk. Buffett and Munger are often associated with focused decisions and strong conviction. But copying concentration without copying skill, patience, emotional control, and deep understanding is risky. A concentrated mistake can hurt much more than a small mistake. This does not mean concentration is always wrong. It means the ability to handle it is not automatic.\n\nThe better way to use this school is to borrow the judgment, not the costume. Learn to avoid dumb mistakes. Learn to watch incentives. Learn to know the edge of your understanding. Learn to separate price from value. Learn to be patient only when the reason still holds. You do not have to copy the exact strategy for the worldview to improve your thinking.\n\nInside Nisk, this lesson should push humility. Test assumptions. Compare worldviews. Look at your real plan. Ask whether you are using a principle as a decision tool or as an identity badge. If you cannot explain the business, the risk, the price, and the reason in plain language, the lens would tell you to slow down.\n\nThe point is not to reject Buffett and Munger. The point is to respect the difficulty of what they teach. Borrow the discipline. Borrow the patience. Borrow the suspicion of nonsense. But do not confuse wearing the language with having the skill.',
			flashcards: [
				{
					id: 'school-buffett-munger-05-fc-1',
					role: 'main_idea',
					front: 'How is this lens misused?',
					back: 'People copy the language without the skill, discipline, or humility.',
				},
				{
					id: 'school-buffett-munger-05-fc-2',
					role: 'watch_out',
					front: 'What is the costume trap?',
					back: 'Sounding like a patient owner while acting like a speculator.',
				},
				{
					id: 'school-buffett-munger-05-fc-3',
					role: 'use_it',
					front: 'How do you stay humble?',
					back: 'Test assumptions, define limits, and avoid making the lens an identity.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Compare lens to reality',
			},
			revision: 1,
		},
	],
};
